import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import InputWithIcon from '../../components/generics/input-with-icon';
import PasswordInput from '../../components/generics/password-input';
import AuthButton from '../../components/auth/auth-button';
import AlertMessage from '../../components/alert-message';
import NavigationLink from '../../components/generics/navigation-link';
import {useAuthViewModel} from "../../view-model/auth-model-view"
import LanguageSwitcher from '../../components/generics/language-switcher';
import userStore from '../../store/session-user-store';

const LoginPage: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const authViewModel = useAuthViewModel();

  const currentLang = i18n.language || 'fr';

  // Rediriger si l'utilisateur est déjà connecté
  useEffect(() => {
    if (userStore.isUserAuthenticated) {
      window.location.href = `/${currentLang}/provider/dashboard`;
    }
  }, [currentLang]);

  // Ne pas afficher le formulaire si l'utilisateur est authentifié (pendant la redirection)
  if (userStore.isUserAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center overflow-hidden py-4 md:py-8 px-4 relative">
      {/* Représentation subtile de calendrier/planning en arrière-plan */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none">
        {/* Grille de planning stylisée */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, rgb(35, 168, 146) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(35, 168, 146) 1px, transparent 1px)
          `,
          backgroundSize: '80px 60px',
          transform: 'perspective(800px) rotateX(45deg) scale(1.5)',
          transformOrigin: 'center center',
        }}></div>
      </div>

      <div className="w-full max-w-xl bg-white/95 backdrop-blur-md p-4 md:p-8 shadow-2xl rounded-2xl border border-gray-100 relative z-10">
        {/* Formulaire sans défilement interne */}
        <div className="w-full p-4 md:p-8">
          <div className="mb-6 flex items-center justify-between">
            <a href={`${process.env.REACT_APP_HOME_URL}`} className="flex items-center">
              <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-7 sm:h-8 md:h-10" />
            </a>
            <LanguageSwitcher />
          </div>
          
          <div className="mb-6 text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">{t('common.welcome')}</h1>
            <p className="text-base md:text-lg text-gray-600">{t('auth.loginTitle')}</p>
          </div>
          
          {authViewModel.errorMessage && (
            <AlertMessage message={authViewModel.errorMessage} type="error" />
          )}

          <form onSubmit={(e)=> authViewModel.login(e)} className="space-y-5">
            <InputWithIcon
              label={t('common.email')}
              type="email"
              id="email"
              value={authViewModel.email}
              onChange={e => authViewModel.setEmail(e.target.value)}
              required
              placeholder="votre@email.com"
              disableDarkMode={true}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
              }
            />
            
            <PasswordInput
              label={t('common.password')}
              id="password"
              name="password"
              value={authViewModel.password}
              onChange={e => authViewModel.setPassword(e.target.value)}
              required
              placeholder={t('auth.passwordPlaceholder', 'Votre mot de passe') || 'Votre mot de passe'}
              autoComplete="current-password"
              forceLightMode={true}
            />
            
            <div className="flex items-center justify-center">
              <NavigationLink
                to="/forgot-password"
                className="text-sm text-primary hover:text-primary-dark transition-colors duration-300"
              >
                {t('common.forgotPassword')}
              </NavigationLink>
            </div>
            
            <AuthButton type="submit">{t('common.loginAction')}</AuthButton>
          </form>
          
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>{t('common.dontHaveAccount')} <NavigationLink to="/signup" className="text-primary font-medium hover:text-primary-dark transition-colors duration-300">{t('common.signup')}</NavigationLink></p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default LoginPage;
